import { buttonStyle } from 'components/buttons/buttonStyle';
import NumberInput from 'components/forms/inputs/rhf-inputs/NumberInput.tsx';
import { DataTable } from 'components/tables/data-table';
import { Button } from 'components/ui/button';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { useUpdateHideInvoicesOlderThanDaysMutation } from 'store/api/tenants.ts';
import { HideInvoicesOlderThanDaysUpdateDto, TenantConfigurationDto } from 'types/tenants.ts';
import { t } from 'utils/getTranslations.ts';
import { useHasPermissions } from 'utils/permissionUtil';

interface Props {
    tenantConfiguration: TenantConfigurationDto;
}

const HideInvoicesOlderThanDaysControl = ({ tenantConfiguration }: Props): JSX.Element => {
    const [updateHideInvoicesOlderThanDays] = useUpdateHideInvoicesOlderThanDaysMutation();
    const hasUpdatePermission = useHasPermissions(['UPDATE.BACK_OFFICE_SETTINGS']);

    const { handleSubmit, control } = useForm<HideInvoicesOlderThanDaysUpdateDto>({
        defaultValues: {
            hideInvoicesOlderThanDays: tenantConfiguration.hideInvoicesOlderThanDays ?? null,
        },
    });

    const columns = useMemo<DataTableColumnDef<TenantConfigurationDto>[]>(
        () => [
            {
                header: t('configuration.hide-invoices-older-than-days'),
                id: 'hide-invoices-older-than-days',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 300,
                },
                cell: () => (
                    <div className="flex gap-4">
                        <NumberInput
                            control={control}
                            name="hideInvoicesOlderThanDays"
                            inputProps={{ min: 0 }}
                            disabled={!hasUpdatePermission}
                        />
                        <Button
                            {...buttonStyle({ variant: 'contained', color: 'secondary' }, 'h-9')}
                            onClick={() => {
                                void handleSubmit((data) => updateHideInvoicesOlderThanDays(data))();
                            }}
                            disabled={!hasUpdatePermission}
                        >
                            <FormattedMessage id="save" />
                        </Button>
                    </div>
                ),
            },
            {
                header: '',
                id: 'spacer',
                enableSorting: false,
                meta: {
                    widthWeight: 3,
                },
                cell: () => null,
            },
        ],
        [control, handleSubmit, hasUpdatePermission, updateHideInvoicesOlderThanDays]
    );

    const rows = useMemo(() => [tenantConfiguration], [tenantConfiguration]);

    return (
        <>
            <div className="flex justify-between">
                <h3 className="text-h3 text-balance">
                    <FormattedMessage id="configuration.hide-invoices-older-than-days-control" />
                </h3>
            </div>
            <DataTable<TenantConfigurationDto>
                columns={columns}
                data={rows}
                columnConfigId="configuration-hide-invoices-older-than-days"
            />
        </>
    );
};

export default HideInvoicesOlderThanDaysControl;
